import { usePathname } from "next/navigation";

import { BALANCES_POLLING_INTERVAL_MS } from "@/constants/polling";
import { useGetCustomerAccountBalancesQuery } from "@/generated/redux/services/generated/api";
import { isTestEnv } from "@/lib/env";
import { useCustomerAccount } from "@/providers";

import { shouldPollOnPage } from "./usePollingPage";

// Module-scoped constant so the fallback reference is stable across calls —
// `{}` inline would make every render bust downstream memo chains.
const EMPTY_BALANCES: Record<string, never> = {};

/**
 * Hook to fetch customer account balances from the BFF.
 * Returns a map of product code to balance information.
 */
export function useBalances() {
  const { customerAccount } = useCustomerAccount();
  const customerAccountId = customerAccount?.customerAccount?.id;
  const pathname = usePathname();
  const shouldPoll = shouldPollOnPage(pathname);

  const { data, isLoading, isError, refetch, isUninitialized, isFetching } =
    useGetCustomerAccountBalancesQuery(
      { customerAccountId: customerAccountId as string },
      {
        skip: !customerAccountId,
        pollingInterval:
          isTestEnv || !shouldPoll ? undefined : BALANCES_POLLING_INTERVAL_MS,
        skipPollingIfUnfocused: true,
      }
    );

  return {
    balances: data?.balances ?? EMPTY_BALANCES,
    isLoading,
    isError,
    refetch,
    isUninitialized,
    isFetching,
  };
}
